import { inject, Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { CreateReceipt, ENVIRONMENT, Invoice, Match, Receipt, ReceiptStatus } from '@models';
import { combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { ClubsService } from './club.service';
import { UserService } from './current-user.service';
import { FirestoreService } from './firestore.service';
import { resolveFunctionsBaseUrl } from './functions-base-url';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';
import { UsersService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class InvoicesService {
  private readonly firestore = inject(FirestoreService);
  private readonly seasonManagerService = inject(SeasonManagerService);
  private readonly functions = inject(Functions);
  private readonly clubsService = inject(ClubsService);
  private readonly usersService = inject(UsersService);
  private readonly currentUserService = inject(UserService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly environment = inject<any>(ENVIRONMENT);
  private readonly firebaseApp = inject(FirebaseApp);

  private get functionsBaseUrl(): string {
    return resolveFunctionsBaseUrl(this.firebaseApp, this.environment);
  }

  private get cols() {
    return this.tenantContext.collections;
  }

  public invoices$ = combineLatest([this.seasonManagerService.season$, this.clubsService.all$]).pipe(
    switchMap(([seasonYear, clubs]) =>
      this.firestore
        .listDocuments<Invoice>(this.cols.INVOICES, where('seasonYear', '==', seasonYear), orderBy('createdAt', 'desc'))
        .pipe(
          map((invoices) =>
            invoices.map((invoice) => ({
              ...invoice,
              club: clubs.find((club) => club.id === invoice.clubId),
            })),
          ),
        ),
    ),
    shareReplay(1),
  );

  public receipts$ = combineLatest([
    this.seasonManagerService.season$,
    this.clubsService.all$,
    this.usersService.users$,
  ]).pipe(
    switchMap(([seasonYear, clubs, users]) =>
      this.firestore
        .listDocuments<Receipt>(this.cols.RECEIPTS, where('seasonYear', '==', seasonYear), orderBy('createdAt', 'desc'))
        .pipe(
          map((receipts) =>
            receipts.map((receipt) => ({
              receipt,
              club: clubs.find((club) => club.id === receipt.clubId),
              user: users.find((user) => user.id === receipt.userId) ?? null,
            })),
          ),
        ),
    ),
    shareReplay(1),
  );

  public myReceipts$ = combineLatest([this.seasonManagerService.season$, this.currentUserService.uid$]).pipe(
    switchMap(([seasonYear, uid]) =>
      this.firestore.listDocuments<Receipt | CreateReceipt>(
        this.cols.RECEIPTS,
        where('seasonYear', '==', seasonYear),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
      ),
    ),
    shareReplay(1),
  );

  downloadReceipt(receiptId: string): Promise<void> {
    const tenantId = this.tenantContext.tenantId;
    const url = `${this.functionsBaseUrl}receipt?receiptId=${receiptId}&tenantId=${tenantId}`;
    window.open(url, '_blank');
    return Promise.resolve();
  }

  async markReceiptsAsPaid(receiptsIds: string[]): Promise<void> {
    for (const receiptId of receiptsIds) {
      await this.firestore.update(this.cols.RECEIPTS, {
        id: receiptId,
        status: ReceiptStatus.FEDERATION_PAID,
        paymentDate: new Date(),
      });
    }
  }

  async updateReceipt(receipt: Partial<Receipt>): Promise<void> {
    return this.firestore.update<Receipt>(this.cols.RECEIPTS, receipt);
  }

  async deleteReceipt(receiptId: string): Promise<void> {
    return this.firestore.deleteForever(this.cols.RECEIPTS, receiptId);
  }

  getReceipt(receiptId: string) {
    return this.firestore.getDocument<Receipt>(this.cols.RECEIPTS, receiptId);
  }

  /**
   * Recibo del usuario logado para un partido concreto.
   *
   * IMPORTANTE: la regla de Firestore de `receipts` solo permite la lectura a
   * un miembro cuando `resource.data.userId == request.auth.uid`. Una query que
   * filtra únicamente por `matchId` NO satisface esa condición y Firestore la
   * rechaza con `permission-denied`; combinado con la resuscripción de
   * `collectionData`/AngularFire esto disparaba el bug interno del SDK
   * (`INTERNAL ASSERTION FAILED (ca9)` + `UnsubscriptionError`) en bucle,
   * congelando la pantalla de detalle de partido. Añadir `userId == uid` hace
   * la query conforme a la regla y elimina el bucle.
   */
  getReceiptByMatch(matchId: string) {
    return this.currentUserService.uid$.pipe(
      switchMap((uid) =>
        this.firestore.findDocument<Receipt>(
          this.cols.RECEIPTS,
          where('matchId', '==', matchId),
          where('userId', '==', uid),
        ),
      ),
    );
  }

  downloadInvoice(invoiceId: string): Promise<void> {
    const tenantId = this.tenantContext.tenantId;
    const url = `${this.functionsBaseUrl}invoice?invoiceId=${invoiceId}&tenantId=${tenantId}`;
    window.open(url, '_blank');
    return Promise.resolve();
  }

  /**
   * Borra una factura **a través de la Cloud Function** `deleteInvoice`.
   *
   * Antes se borraba el documento directamente (`deleteForever`), y los
   * `movementsIds` de esa factura **nunca volvían a `invoiced: false`**: quedaban
   * facturados para siempre sin factura que los respaldase y, desde que
   * `generateInvoice` valida `invoiced` en servidor, sin forma normal de
   * recuperarlos. El callable hace lo correcto de forma atómica (relee la
   * factura, libera sus movimientos y borra en una única transacción), así que
   * replicar esa lógica en el cliente sería a la vez peor y no atómico.
   *
   * Solo se envía el `id`: los movimientos que se liberan salen del documento
   * almacenado en servidor, nunca de aquí (#711).
   *
   * Lanza si el borrado no se completó, para que la pantalla NO cante un éxito
   * que no ha ocurrido: los rechazos explícitos llegan como `HttpsError` con su
   * código (`not-found`, `permission-denied`) y los fallos de infraestructura
   * como el `false` que devuelve el callable.
   */
  async deleteInvoice(invoice: Invoice): Promise<void> {
    const callable = httpsCallable<{ id: string }, boolean>(
      this.functions,
      'deleteInvoice',
    );
    const deleted = await callable({ id: invoice.id });
    if (deleted.data !== true) {
      throw new Error(`deleteInvoice returned ${deleted.data}`);
    }
  }

  /**
   * Partidos de la temporada actual asignados al árbitro logado que aún NO tienen
   * recibo. Devuelve `Match[]` crudo (la pantalla create-payment usa
   * `match.localTeamId/visitorTeamId/seasonId` y `[match]`). Sin filtro de
   * `status`/`date`: incluye partidos futuros asignados, excluyendo solo los que
   * ya tienen recibo (por `matchId`).
   */
  public refereeMatchesWithoutReceipt$ = combineLatest([
    this.seasonManagerService.season$,
    this.currentUserService.uid$,
    this.myReceipts$,
  ]).pipe(
    switchMap(([seasonYear, uid, myReceipts]) =>
      this.firestore
        .listDocuments<Match>(
          this.cols.MATCHES,
          where('refereeId', '==', uid),
          where('seasonYear', '==', seasonYear),
          orderBy('date', 'asc'),
        )
        .pipe(
          map((matches) =>
            matches.filter((match) => !myReceipts.some((r) => 'matchId' in r && r.matchId === match.id)),
          ),
        ),
    ),
    shareReplay(1),
  );
}
